"use client";

import { Badge } from "@/components/ui/badge";
import { StackAdjustmentInputs, UtilizationBar } from "./results-controls";
import {
  computeResultTotals,
  getAdditionalItemLabel,
  type ComputedTotals,
  type ResultsProps,
} from "./results-totals";

export function Results(props: ResultsProps) {
  const {
    result,
    hasWeight,
    perFloor,
    stackLayers,
    singularLabel,
    pluralLabel,
    specificQtyEnabled,
    onPerFloorChange,
    onStackLayersChange,
  } = props;
  const totals = computeResultTotals(props);

  return (
    <div className="space-y-4">
        <ResultsHeadline
          totals={totals}
          perFloor={perFloor}
          stackLayers={stackLayers}
          pluralLabel={pluralLabel}
        />

        {totals.hasAdditionalItems && (
          <LoadManifest
            totals={totals}
            pluralLabel={pluralLabel}
            perFloor={perFloor}
            stackLayers={stackLayers}
          />
        )}

        <StackAdjustmentInputs
          result={result}
          perFloor={perFloor}
          stackLayers={stackLayers}
          singularLabel={singularLabel}
          onPerFloorChange={onPerFloorChange}
          onStackLayersChange={onStackLayersChange}
        />

        <div className="space-y-3 border-t border-border pt-3">
          <UtilizationBar
            label="Space"
            value={
              specificQtyEnabled &&
              totals.effectiveTotal > 0 &&
              result.totalBySpace > 0
                ? result.spaceUtilization *
                  (totals.effectiveTotal / result.totalBySpace)
                : result.spaceUtilization
            }
            color="bg-action-fill"
          />
          {hasWeight && (
            <UtilizationBar
              label="Weight"
              value={
                specificQtyEnabled &&
                totals.effectiveTotal > 0 &&
                result.totalBySpace > 0
                  ? result.weightUtilization *
                    (totals.effectiveTotal / result.totalBySpace)
                  : result.weightUtilization
              }
              color="bg-warning"
            />
          )}
        </div>
    </div>
  );
}

function LimitBadge({ isWeightLimited }: { isWeightLimited: boolean }) {
  return (
    <Badge variant={isWeightLimited ? "warning" : "outline"} className="mt-2">
      {isWeightLimited ? "Limited by weight" : "Limited by space"}
    </Badge>
  );
}

function TrucksNeededLine({
  trucksNeeded,
  parsedTargetQty,
  pluralLabel,
}: {
  trucksNeeded: number;
  parsedTargetQty: number;
  pluralLabel: string;
}) {
  return (
    <div className="mt-2 text-sm font-medium text-foreground">
      {trucksNeeded.toLocaleString("en-US")}{" "}
      {trucksNeeded === 1 ? "truck" : "trucks"} needed for{" "}
      {parsedTargetQty.toLocaleString("en-US")} {pluralLabel}
    </div>
  );
}

function ResultsHeadline({
  totals,
  perFloor,
  stackLayers,
  pluralLabel,
}: {
  totals: ComputedTotals;
  perFloor: number;
  stackLayers: number;
  pluralLabel: string;
}) {
  const displayTotal = totals.hasAdditionalItems
    ? totals.grandTotal
    : totals.effectiveTotal;
  return (
    <div>
      <div className="text-3xl font-semibold tabular-nums tracking-tight text-foreground">
        {displayTotal === Infinity
          ? "\u2014"
          : displayTotal.toLocaleString("en-US")}
      </div>
      {totals.hasAdditionalItems &&
      totals.grandTotal !== Infinity &&
      totals.grandTotal > 0 ? (
        <p className="text-xs text-muted-foreground mt-1">
          total items per truck
        </p>
      ) : (
        <div className="text-xs text-muted-foreground mt-1">
          {perFloor} per floor &times; {stackLayers} high
        </div>
      )}
      {totals.effectiveTotal > 0 && (
        <LimitBadge isWeightLimited={totals.isWeightLimited} />
      )}
      {totals.trucksNeeded !== null && (
        <TrucksNeededLine
          trucksNeeded={totals.trucksNeeded}
          parsedTargetQty={totals.parsedTargetQty}
          pluralLabel={pluralLabel}
        />
      )}
    </div>
  );
}

function LoadManifest({
  totals,
  pluralLabel,
  perFloor,
  stackLayers,
}: {
  totals: ComputedTotals;
  pluralLabel: string;
  perFloor: number;
  stackLayers: number;
}) {
  return (
    <div className="border-y border-border py-3">
      <p className="text-xs font-medium text-muted-foreground mb-2">
        Load manifest
      </p>
      <ul className="text-sm text-foreground space-y-1">
        <li className="flex justify-between">
          <span>{pluralLabel}</span>
          <span className="font-semibold tabular-nums">
            {totals.effectiveTotal === Infinity
              ? "\u2014"
              : totals.effectiveTotal.toLocaleString("en-US")}
          </span>
        </li>
        {totals.activeAdditionalItems.map((addedItem) => (
          <li key={addedItem.id} className="flex justify-between">
            <span>{getAdditionalItemLabel(addedItem)}</span>
            <span className="font-semibold tabular-nums text-muted-foreground">
              {addedItem.parsedQuantity.toLocaleString("en-US")}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-2 pt-2 border-t border-border flex justify-between text-xs text-muted-foreground">
        <span>{pluralLabel} layout</span>
        <span className="tabular-nums">
          {perFloor} per floor &times; {stackLayers} high
        </span>
      </div>
    </div>
  );
}
